import { z } from "zod";
import { ProblemCategorySchema, ProblemClinicalStatusSchema } from "./chart-history.js";
import { AllergySeveritySchema, AllergyStatusSchema, FamilyRelationSchema, HistoryRowStatusSchema, ImmunizationRouteSchema, ImmunizationStatusSchema, MedicationStatusSchema, SocialHistoryKindSchema, SocialHistoryStatusSchema, } from "./patient-profile.js";
import { IdSchema } from "./primitives.js";
export const PATIENT_PROFILE_ASSISTANT_PROMPT_VERSION = "patient-profile-assistant-v1";
export const PATIENT_PROFILE_ASSISTANT_MAX_AUDIO_BYTES = 6_000_000;
export const PATIENT_PROFILE_ASSISTANT_MAX_MESSAGE_LENGTH = 8_000;
const display = z.string().trim().min(1).max(240);
const note = z.string().trim().min(1).max(2_000).optional();
const sourceText = z.string().min(1).max(PATIENT_PROFILE_ASSISTANT_MAX_MESSAGE_LENGTH);
const message = z.string().trim().min(1).max(PATIENT_PROFILE_ASSISTANT_MAX_MESSAGE_LENGTH);
/** Additions only. Identifiers, codes, provenance and prescription fields are deliberately absent. */
export const PatientProfileAssistantOperationSchema = z.discriminatedUnion("section", [
    z
        .object({
        section: z.literal("medications"),
        sourceText,
        entry: z
            .object({
            display,
            sig: z.string().trim().min(1).max(500).optional(),
            status: MedicationStatusSchema.optional(),
        })
            .strict(),
    })
        .strict(),
    z
        .object({
        section: z.literal("pmh"),
        sourceText,
        entry: z
            .object({
            display,
            category: ProblemCategorySchema.optional(),
            status: ProblemClinicalStatusSchema.optional(),
            onsetDate: z.string().date().optional(),
            note,
        })
            .strict(),
    })
        .strict(),
    z
        .object({
        section: z.literal("allergies"),
        sourceText,
        entry: z
            .object({
            display,
            reaction: z.string().trim().min(1).max(500).optional(),
            severity: AllergySeveritySchema.optional(),
            status: AllergyStatusSchema.optional(),
        })
            .strict(),
    })
        .strict(),
    z
        .object({
        section: z.literal("familyHistory"),
        sourceText,
        entry: z
            .object({
            relation: FamilyRelationSchema,
            display,
            onsetAge: z.number().int().min(0).max(130).optional(),
            deceased: z.boolean().optional(),
            note,
            status: HistoryRowStatusSchema.optional(),
        })
            .strict(),
    })
        .strict(),
    z
        .object({
        section: z.literal("socialHistory"),
        sourceText,
        entry: z
            .object({
            kind: SocialHistoryKindSchema,
            status: SocialHistoryStatusSchema,
            detail: z.string().trim().min(1).max(500),
            quantity: z.string().trim().min(1).max(120).optional(),
            frequency: z.string().trim().min(1).max(120).optional(),
            startedOn: z.string().date().optional(),
            endedOn: z.string().date().optional(),
            note,
            clinicalStatus: HistoryRowStatusSchema.optional(),
        })
            .strict(),
    })
        .strict(),
    z
        .object({
        section: z.literal("immunizations"),
        sourceText,
        entry: z
            .object({
            display,
            administeredOn: z.string().date(),
            lotNumber: z.string().trim().min(1).max(80).optional(),
            site: z.string().trim().min(1).max(80).optional(),
            route: ImmunizationRouteSchema.optional(),
            doseNumber: z.number().int().min(1).max(20).optional(),
            note,
            status: ImmunizationStatusSchema.optional(),
        })
            .strict(),
    })
        .strict(),
]);
export const PatientProfileAssistantInterpretRequestSchema = z
    .object({
    siteId: IdSchema,
    message,
    /** Keep the original instruction across clarification; a reply may only correct a drug name. */
    clarification: z
        .object({ originalMessage: message, question: z.string().trim().min(1).max(1_000) })
        .strict()
        .optional(),
})
    .strict();
export const PatientProfileAssistantInterpretResponseSchema = z.discriminatedUnion("status", [
    z
        .object({
        status: z.literal("ready"),
        message: z.string().trim().min(1).max(1_000),
        operations: z.array(PatientProfileAssistantOperationSchema).min(1).max(12),
    })
        .strict(),
    z
        .object({
        status: z.literal("clarification"),
        message: z.string().trim().min(1).max(1_000),
        operations: z.array(PatientProfileAssistantOperationSchema).max(0),
    })
        .strict(),
    z
        .object({
        status: z.literal("unavailable"),
        message: z.string().trim().min(1).max(1_000),
        operations: z.array(PatientProfileAssistantOperationSchema).max(0),
    })
        .strict(),
]);
export const PatientProfileAssistantTranscribeRequestSchema = z
    .object({
    siteId: IdSchema,
    audioBase64: z
        .string()
        .min(4)
        .max(8_000_000)
        .regex(/^[A-Za-z0-9+/]+={0,2}$/)
        .refine((value) => value.length % 4 === 0, "Audio must use padded base64 encoding"),
    mimeType: z
        .string()
        .trim()
        .regex(/^audio\/(?:webm|mp4|mpeg|mp3|mpga|m4a|x-m4a|wav|x-wav)(?:;.*)?$/i),
})
    .strict();
export const PatientProfileAssistantTranscribeResponseSchema = z
    .object({ transcript: message })
    .strict();
//# sourceMappingURL=patient-profile-assistant.js.map